import React, { useContext } from 'react';
import { AppContext } from '~/providers/AppProvider';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { placeholderImages, getHashedImageSrc } from '~/utils/images';
import type { BoatThumbnailDto } from '~/api/client/api';
import { BoatCard } from '@boatsetter-backup/js-component-lib';
import { getCurrencyString } from '~/utils/currencyUtils';
import { Trans, withI18n } from '@lingui/react';
import { t } from '@lingui/macro';
import moment from 'moment';
import { getCultureToken } from '~/utils/langUtils';
import getConfig from 'next/config';
import { slugifyToURL } from './utils';
const { publicRuntimeConfig } = getConfig();
interface Props {
  boatData: BoatThumbnailDto;
  isHorizontalView: boolean;
  hoveredResultId: string;
  setHoveredResultId: any;
  i18n: any;
}

const BoatItem = ({ boatData, isHorizontalView, hoveredResultId, setHoveredResultId, i18n }: Props) => {
  const router = useRouter();
  const appCtx = useContext(AppContext);

  const {
    Id,
    Alias,
    Picture,
    Length,
    Name,
    FromPrice,
    FromPriceCurrency,
    LocationName,
    Passengers,
    LocationLatitude,
    LocationLongitude,
    Gallery,
    InstantBookable,
    LocationId,
  } = boatData;

  const getBoatImages = (Gallery: Object[]) => {
    const images = [];

    if (Gallery.length !== 0) {
      Gallery.forEach((picture) => {
        images.push(getHashedImageSrc(picture));
      });
    } else {
      return [placeholderImages.boat];
    }

    return images;
  };

  const searchResult = {
    boat_public_id: String(Alias),
    location_id: String(LocationId),

    title: Name,
    display_price: (
      <>
        <Trans id="from" /> {getCurrencyString(FromPriceCurrency, FromPrice, 0, 0)}
      </>
    ),
    instant_bookable: InstantBookable,
    max_passengers: Passengers,
    location: {
      display_location: LocationName,
      lat: LocationLatitude,
      lng: LocationLongitude,
    },
    display_size: `${Length} ${i18n._(t`global.feet`)}`,
    photos: getBoatImages(Gallery),
  };

  const getBoatDetailUrl = (boatId, locationId) => {
    const hasDate =
      router.query.availableFrom === undefined
        ? ``
        : `&availableFrom=${moment(router.query.availableFrom).format('YYYY-MM-DD')}`;

    const hasPassengers =
      router.query.passengerCount === undefined ? `` : `&passengerCount=${router.query.passengerCount}`;

    const currencyUrlPart =
      appCtx.state.defaultCurrency === appCtx.state.currency ? '' : `currency=${appCtx.state.currency}`;

    const hasQueries = hasDate || hasPassengers || currencyUrlPart ? '?' : '';

    return `${publicRuntimeConfig.ASSETS_PREFIX}/${getCultureToken(appCtx)}boats/${`${Alias}-${slugifyToURL(
      LocationName
    )}-${Id}-${locationId}`}${hasQueries}${currencyUrlPart}${hasDate}${hasPassengers}`;
  };

  return (
    <Wrapper isHorizontalView={isHorizontalView}>
      <BoatCard
        searchResult={searchResult}
        photos={searchResult.photos}
        index={1}
        hoveredResultId={hoveredResultId}
        setHoveredResultId={setHoveredResultId}
        horizontalCard={isHorizontalView}
        getBoatDetailUrl={getBoatDetailUrl}
        isFavorite={false}
        showIsFavorite={false}
        showRating={false}
        isLoading={appCtx.state.loading}
        upToString={<Trans id="boats.keyData.passengers.first" />}
        passengersString={<Trans id="boats.keyData.passengers.second" />}
        isGlobal={true}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div<{ isHorizontalView: boolean }>`
  flex: 0 0 100%;
  box-sizing: border-box !important;
  flex-basis: auto !important;

  @media only screen and (max-width: 640px) and (min-width: 320px) {
    width: 100%;
    display: block !important;
    padding-left: 0em;
    padding-right: 0em;
  }

  @media only screen and (min-width: 960px) {
    width: 33.33333%;
  }

  @media only screen and (max-width: 1200px) and (min-width: 650px) {
    width: 50%;
  }

  width: ${(props) => (props.isHorizontalView ? '100% !important' : 'inherit')};
  padding: 0 25px;
  padding-left: 3em;
  padding-right: 2.5em;
  -webkit-border-radius: 16px;
  border-radius: 16px;

  div[class*='carousel-slider'] {
    border-radius: ${(props) => (props.isHorizontalView ? '16px 0px 0px 16px ' : '16px 14px 0px 0px')};
    -webkit-border-radius: ${(props) => (props.isHorizontalView ? '16px 0px 0px 16px ' : '16px 14px 0px 0px')};
  }

  div[class*='BoatCard_BoatCardWrapper'] {
    background: transparent;
    border-radius: 16px;
    -webkit-border-radius: 16px;
  }

  li[class*='slide selected'] > div > div {
    border-radius: ${(props) => (props.isHorizontalView ? '16px 0px 0px 16px ' : '16px 14px 0px 0px')};
    -webkit-border-radius: ${(props) => (props.isHorizontalView ? '16px 0px 0px 16px ' : '16px 14px 0px 0px')};
  }
`;

export default withI18n()(BoatItem);
